import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AppIcon } from './ui/icon'
import { mdiRobotOutline, mdiLightbulbOnOutline, mdiCheck, mdiClose, mdiLoading } from '@mdi/js'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog'
import { $api } from '../api/queryClient'
import { refreshDocument } from '../hooks/useQueries'

interface DocumentTypeProposalCardProps {
    org: string
    docUid: string
    document: any
    // Names of existing document types keyed by uid, used to render the suggestion.
    documentTypeName: (uid: string | undefined) => string
    // Classify the document with an existing type (reuses the manual-classify PATCH).
    onClassifyWithType: (typeUid: string) => Promise<void>
    // Optional: open the existing classification picker ("Choose another…").
    onChooseAnother?: () => void
}

interface ProposalNewProp {
    code: string
    name?: Record<string, string>
    description?: Record<string, string>
    type?: string
}

interface ProposalPayload {
    code?: string
    name?: Record<string, string>
    description?: Record<string, string>
    parent_document_type_code?: string
    existing_property_codes?: string[]
    new_properties?: ProposalNewProp[]
    reasoning?: string
}

// DocumentTypeProposalCard renders the three LLM-classification review states on
// the document detail page:
//   1. processing indicator (document_type_llm_search in processing_flags),
//   2. suggestion banner (suggested_document_type_uid property, doc untyped),
//   3. new-type proposal review card (document_type_proposal flag).
export function DocumentTypeProposalCard({
    org,
    docUid,
    document,
    documentTypeName,
    onClassifyWithType,
    onChooseAnother,
}: DocumentTypeProposalCardProps) {
    const { t } = useTranslation('documents')
    const queryClient = useQueryClient()

    const flags: string[] = (document?.flags as string[]) || []
    const processingFlags: string[] = (document?.processing_flags as string[]) || []
    const properties = (document?.properties as Record<string, any>) || {}
    const isTyped = !!document?.document_type_uid

    const hasProposal = flags.includes('document_type_proposal')
    const isProcessing = processingFlags.includes('document_type_llm_search')
    const suggestedUid: string | undefined = properties.suggested_document_type_uid
    const proposal: ProposalPayload | undefined = properties.document_type_proposal

    const [notice, setNotice] = useState<{ tone: 'success' | 'error'; message: string; detail?: string } | null>(null)
    const [dismissOpen, setDismissOpen] = useState(false)

    // Editable proposal fields (spec: all fields editable before acceptance).
    const [code, setCode] = useState('')
    const [nameEn, setNameEn] = useState('')
    const [nameFr, setNameFr] = useState('')
    const [descEn, setDescEn] = useState('')
    const [descFr, setDescFr] = useState('')
    const [parentCode, setParentCode] = useState('')

    useEffect(() => {
        if (proposal) {
            setCode((proposal.code || '').toUpperCase())
            setNameEn(proposal.name?.en || '')
            setNameFr(proposal.name?.fr || '')
            setDescEn(proposal.description?.en || '')
            setDescFr(proposal.description?.fr || '')
            setParentCode(proposal.parent_document_type_code || '')
        }
    }, [proposal])

    const acceptMutation = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/document-type-proposal/accept'
    )
    const dismissMutation = $api.useMutation(
        'delete',
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/document-type-proposal'
    )

    const existingCodes = useMemo(() => proposal?.existing_property_codes || [], [proposal])
    const newProps = useMemo(() => proposal?.new_properties || [], [proposal])

    const handleAccept = async () => {
        try {
            await acceptMutation.mutateAsync({
                params: { path: { orgCode: org, documentUid: docUid } },
                body: {
                    code,
                    name: { en: nameEn, fr: nameFr },
                    description: { en: descEn, fr: descFr },
                    parent_document_type_code: parentCode || undefined,
                    existing_property_codes: existingCodes,
                    new_properties: newProps as any,
                },
            })
            refreshDocument(queryClient, org, docUid)
            queryClient.invalidateQueries({ queryKey: ['documentTypes', org] })
            setNotice({ tone: 'success', message: t('proposal.accepted') })
        } catch (e: any) {
            // openapi-fetch throws the parsed error body itself (not a Response),
            // so the error code — not the HTTP status — is what tells the
            // failure modes apart (spec 2026-09-06-03, spec 2026-09-06-14):
            // the type's own code colliding, a proposed new property's code
            // already existing, a validation failure (bad property type,
            // missing name/code), or a genuine server-side error. Keep the
            // headline short (361 px viewports in the wild) and put anything
            // server-specific on a second, muted detail line.
            let message = t('proposal.error_accept')
            let detail: string | undefined
            if (e?.code === 'CODE_ALREADY_EXISTS') {
                message = t('proposal.error_code_exists')
            } else if (e?.code === 'PROPERTY_CODE_ALREADY_EXISTS') {
                message = t('proposal.error_property_code_exists')
            } else if (e?.code === 'VALIDATION_ERROR') {
                detail = e?.fields?.[0]?.message || undefined
            } else if (e?.code === 'INTERNAL_ERROR') {
                message = t('proposal.error_accept_server')
            } else if (e?.title) {
                detail = e.title
            }
            setNotice({ tone: 'error', message, detail })
        }
    }

    const handleDismiss = async () => {
        setDismissOpen(false)
        try {
            await dismissMutation.mutateAsync({ params: { path: { orgCode: org, documentUid: docUid } } })
            refreshDocument(queryClient, org, docUid)
            setNotice({ tone: 'success', message: t('proposal.dismissed') })
        } catch {
            setNotice({ tone: 'error', message: t('proposal.error_dismiss') })
        }
    }

    // Processing indicator (only when nothing else to show).
    if (isProcessing && !hasProposal && !suggestedUid && !isTyped) {
        return (
            <div
                role="status"
                data-testid="llm-classification-processing"
                className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-foreground"
            >
                <AppIcon path={mdiLoading} size="md" spin className="text-primary" />
                <span>{t('proposal.processing')}</span>
            </div>
        )
    }

    // Suggestion banner (doc untyped, has a suggested type).
    if (!hasProposal && suggestedUid && !isTyped) {
        return (
            <div
                data-testid="llm-suggestion-banner"
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
            >
                <span className="flex items-center gap-2">
                    <AppIcon path={mdiLightbulbOnOutline} size="md" className="text-amber-600" />
                    {t('proposal.suggests', { type: documentTypeName(suggestedUid) })}
                </span>
                <span className="flex items-center gap-2">
                    <Button
                        size="sm"
                        data-testid="llm-suggestion-accept"
                        onClick={() => onClassifyWithType(suggestedUid)}
                    >
                        <AppIcon path={mdiCheck} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('proposal.accept_suggestion')}</span>
                    </Button>
                    {onChooseAnother && (
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="llm-suggestion-choose"
                            onClick={onChooseAnother}
                        >
                            {t('proposal.choose_another')}
                        </Button>
                    )}
                </span>
            </div>
        )
    }

    // Proposal review card.
    if (hasProposal && proposal) {
        return (
            <div
                data-testid="llm-proposal-card"
                className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-4"
            >
                <div className="flex items-center gap-2">
                    <AppIcon path={mdiRobotOutline} size="md" className="text-primary" />
                    <h3 className="font-semibold">{t('proposal.title')}</h3>
                    <Badge variant="info" className="text-xs">{t('proposal.new_type')}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{t('proposal.subtitle')}</p>

                {notice && (
                    <div
                        role="status"
                        data-testid="proposal-notice"
                        className={`rounded-md border px-3 py-2 text-sm ${notice.tone === 'error'
                            ? 'border-destructive/40 bg-destructive/10 text-destructive'
                            : 'border-primary/40 bg-primary/10 text-foreground'}`}
                    >
                        <div>{notice.message}</div>
                        {notice.detail && (
                            <div data-testid="proposal-notice-detail" className="mt-1 text-xs font-normal text-muted-foreground">
                                {notice.detail}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="proposal-code">{t('proposal.field_code')}</Label>
                        <Input
                            id="proposal-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            data-testid="proposal-code-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="proposal-parent">{t('proposal.field_parent')}</Label>
                        <Input
                            id="proposal-parent"
                            value={parentCode}
                            onChange={(e) => setParentCode(e.target.value.toUpperCase())}
                            placeholder={t('proposal.field_parent_placeholder')}
                            data-testid="proposal-parent-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="proposal-name-en">🇺🇸 {t('proposal.field_name')}</Label>
                        <Input id="proposal-name-en" value={nameEn} onChange={(e) => setNameEn(e.target.value)} data-testid="proposal-name-en" />
                    </div>
                    <div>
                        <Label htmlFor="proposal-name-fr">🇫🇷 {t('proposal.field_name')}</Label>
                        <Input id="proposal-name-fr" value={nameFr} onChange={(e) => setNameFr(e.target.value)} data-testid="proposal-name-fr" />
                    </div>
                    <div>
                        <Label htmlFor="proposal-desc-en">🇺🇸 {t('proposal.field_description')}</Label>
                        <Input id="proposal-desc-en" value={descEn} onChange={(e) => setDescEn(e.target.value)} data-testid="proposal-desc-en" />
                    </div>
                    <div>
                        <Label htmlFor="proposal-desc-fr">🇫🇷 {t('proposal.field_description')}</Label>
                        <Input id="proposal-desc-fr" value={descFr} onChange={(e) => setDescFr(e.target.value)} data-testid="proposal-desc-fr" />
                    </div>
                </div>

                {(existingCodes.length > 0 || newProps.length > 0) && (
                    <div className="text-sm">
                        <span className="text-muted-foreground">{t('proposal.properties')}: </span>
                        {existingCodes.map((c) => (
                            <Badge key={c} variant="secondary" className="mr-1 text-xs" data-testid={`proposal-existing-prop-${c}`}>{c}</Badge>
                        ))}
                        {newProps.map((p) => (
                            <Badge key={p.code} variant="outline" className="mr-1 text-xs" data-testid={`proposal-new-prop-${p.code}`}>
                                {p.code} ({t('proposal.new')})
                            </Badge>
                        ))}
                    </div>
                )}

                {proposal.reasoning && (
                    <p className="text-xs italic text-muted-foreground">{proposal.reasoning}</p>
                )}

                <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                        size="sm"
                        onClick={handleAccept}
                        disabled={acceptMutation.isPending || !code || !nameEn}
                        data-testid="proposal-accept"
                    >
                        {acceptMutation.isPending
                            ? <AppIcon path={mdiLoading} size="sm" spin className="md:mr-1" />
                            : <AppIcon path={mdiCheck} size="sm" className="md:mr-1" />}
                        <span className="hidden md:inline">{t('proposal.create_and_classify')}</span>
                    </Button>
                    {onChooseAnother && (
                        <Button size="sm" variant="outline" onClick={onChooseAnother} data-testid="proposal-classify-existing">
                            {t('proposal.classify_existing')}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDismissOpen(true)}
                        disabled={dismissMutation.isPending}
                        data-testid="proposal-dismiss"
                    >
                        <AppIcon path={mdiClose} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('proposal.dismiss')}</span>
                    </Button>
                </div>

                <AlertDialog open={dismissOpen} onOpenChange={setDismissOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('proposal.dismiss_confirm_title')}</AlertDialogTitle>
                            <AlertDialogDescription>{t('proposal.dismiss_confirm_body')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="proposal-dismiss-cancel">{t('proposal.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDismiss} data-testid="proposal-dismiss-confirm">
                                {t('proposal.dismiss')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        )
    }

    return null
}
